import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Controller, useForm } from 'react-hook-form'
import { Box, Collapse, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import { Autocomplete, BaseDialog } from 'components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import theme from 'theme'
import * as service from 'service'
import constants from 'constants/index'
import useStyles from './styles'

const { LINKABLE_STATUSES_IDS } = constants.governanceProject
const { ACTIVITY_STATUS_LABELS, NOT_LINKABLE_STATUSES_IDS } =
  constants.governanceProjectActivity

const SelectControlModal = ({
  open,
  setOpen,
  documentId,
  refresh = () => {},
}) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()

  const { control, errors, watch, setValue, handleSubmit, reset } = useForm({
    defaultValues: {
      governanceProject: null,
      questionnaire: null,
      question: null,
      controls: [],
    },
  })

  const governanceProject = watch('governanceProject')
  const questionnaire = watch('questionnaire')
  const question = watch('question')
  const controls = watch('controls')

  const {
    response: governanceProjectsResponse,
    isLoading: governanceProjectsIsLoading,
  } = useFetch(
    service.dponet.governanceProject.get,
    {
      linkedDocument: true,
      statuses: LINKABLE_STATUSES_IDS,
      order: 'name ASC',
      perPage: 99999,
    },
    [],
    open,
  )

  const governanceProjects =
    governanceProjectsResponse?.data?.governanceProjects || []

  const {
    response: questionnairesResponse,
    isLoading: questionnairesIsLoading,
  } = useFetch(
    service.dponet.governanceProjectQuestionnaire.resume,
    {
      governanceProjectId: governanceProject?.id,
      linkedDocument: true,
      order: 'kind ASC',
      perPage: 99999,
    },
    [governanceProject],
    !!governanceProject?.id,
  )

  const questionnaires =
    questionnairesResponse?.data?.governanceProjectQuestionnaires || []

  const { response: questionsResponse, isLoading: questionsIsLoading } =
    useFetch(
      service.dponet.governanceProjectActivity.list,
      {
        governanceProjectId: governanceProject?.id,
        governanceProjectQuestionnaireId: questionnaire?.id,
        order: 'governance_project_questions.theme ASC',
        perPage: 99999,
      },
      [questionnaire],
      !!questionnaire?.id,
    )

  const questions =
    questionsResponse?.data?.governanceProjectActivities?.map((activity) => ({
      ...activity?.question,
      status: activity?.status,
      activityId: activity?.id,
    })) || []

  const { response: controlsResponse, isLoading: controlsIsLoading } = useFetch(
    service.dponet.governanceProject.listEvidencePlanActions,
    {
      governanceProjectId: governanceProject?.id,
      entityId: question?.activityId,
      order: 'evidences.title ASC',
      perPage: 99999,
    },
    [question],
    !!question?.activityId,
  )

  const controlOptions = controlsResponse?.data?.evidencePlanActions || []

  const handleClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async ({ controls }) => {
    try {
      setLoading(true)

      await service.dponet.privacyPolicies.createLinkedDocuments({
        privacyPolicyId: documentId,
        evidenceIds: controls.map((control) => control?.evidence?.id),
      })

      snackbar.open({
        message: 'Controles vinculados com sucesso!',
        variant: 'success',
      })
      refresh()
      handleClose()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Não foi possível vincular o controle',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setValue('questionnaire', null)
    setValue('question', null)
    setValue('controls', [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governanceProject])

  useEffect(() => {
    setValue('question', null)
    setValue('controls', [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire])

  useEffect(() => {
    setValue('controls', [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question])

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      setOpen={setOpen}
      title="Selecionar controle"
      closeButtonText="Cancelar"
      actionButtonText="Selecionar"
      actionButton={handleSubmit(onSubmit)}
      closeButtonAction={handleClose}
      disableButton={isEmpty(controls)}
      loading={loading}
      dialogSize="sm"
      fullWidth
      accentColor={theme.palette.primary.main}
    >
      <Box display="flex" flexDirection="column">
        <Box mb={2}>
          <Alert severity="info" variant="standard">
            Projetos de governança concluídos, arquivados ou inativos não podem
            ser editados e, por isso, não aparecem nesta listagem.
          </Alert>
        </Box>
        <Controller
          control={control}
          name="governanceProject"
          mode="onChange"
          onChange={([, data]) => data || null}
          as={
            <Autocomplete
              options={governanceProjects}
              loading={governanceProjectsIsLoading}
              getOptionLabel={(option) => option?.name || ''}
              getOptionSelected={(option, value) => option?.id === value?.id}
              textFieldProps={{
                label: 'Projeto de governança',
                error: !!errors?.governanceProject,
                helperText: errors?.governanceProject?.message,
              }}
            />
          }
        />
        <Collapse in={!!governanceProject}>
          <Box mt={2}>
            <Controller
              control={control}
              name="questionnaire"
              mode="onChange"
              onChange={([, data]) => data || null}
              as={
                <Autocomplete
                  options={questionnaires}
                  loading={questionnairesIsLoading}
                  getOptionLabel={(option) =>
                    option
                      ? `${option?.code ? `[${option.code}] ` : ''}${
                          option?.kind || ''
                        }`
                      : ''
                  }
                  getOptionSelected={(option, value) =>
                    option?.id === value?.id
                  }
                  textFieldProps={{
                    label: 'Questionário',
                    error: !!errors?.questionnaire,
                    helperText: errors?.questionnaire?.message,
                  }}
                />
              }
            />
          </Box>
        </Collapse>
        <Collapse in={!!questionnaire}>
          <Box mt={2}>
            <Controller
              control={control}
              name="question"
              mode="onChange"
              onChange={([, data]) => data || null}
              as={
                <Autocomplete
                  options={questions}
                  loading={questionsIsLoading}
                  getOptionLabel={(option) => option?.theme || ''}
                  getOptionSelected={(option, value) =>
                    option?.id === value?.id
                  }
                  getOptionDisabled={(option) =>
                    NOT_LINKABLE_STATUSES_IDS.includes(option?.status)
                  }
                  renderOption={(option) =>
                    NOT_LINKABLE_STATUSES_IDS.includes(option?.status) ? (
                      <Tooltip
                        title={`Esta questão está com status "${
                          ACTIVITY_STATUS_LABELS[option?.status]
                        }" e não pode ser selecionada`}
                      >
                        <Box
                          className={classes.disabledOptionWrapper}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {option?.theme}
                        </Box>
                      </Tooltip>
                    ) : (
                      option?.theme
                    )
                  }
                  textFieldProps={{
                    label: 'Questão',
                    error: !!errors?.question,
                    helperText: errors?.question?.message,
                  }}
                />
              }
            />
          </Box>
        </Collapse>
        <Collapse in={!!question}>
          <Box mt={2}>
            <Controller
              control={control}
              name="controls"
              mode="onChange"
              onChange={([, data]) => data || []}
              as={
                <Autocomplete
                  multiple
                  options={controlOptions}
                  loading={controlsIsLoading}
                  getOptionLabel={(option) =>
                    helpers.governanceProject.mountControlLabel(
                      option?.evidence,
                    )
                  }
                  getOptionSelected={(option, value) =>
                    option?.id === value?.id
                  }
                  textFieldProps={{
                    label: 'Controles',
                    error: !!errors?.controls,
                    helperText: errors?.controls?.message,
                  }}
                />
              }
            />
          </Box>
        </Collapse>
      </Box>
    </BaseDialog>
  )
}

SelectControlModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refresh: PropTypes.func,
}

export default SelectControlModal
